import { ShapeType } from '@/types/commontypes';
import { fansWithColor, levelsPropetyWithColor, retracementsPropetyWithColor } from './defaultColors';
import type { ShapePropsByType } from '@/types/commontypes';
import { VertexType } from '@/types/commontypes';

// Shared defaults for VolumeProfile (FRVP and AVP)
export const volumeProfileDefaults = {
  rowsLayout: 'Number of Rows' as const,
  rowSize: 7,
  volume: 'Up/Down' as const,
  valueAreaVolume: 70,
  volumeProfile: true,
  values: { show: false, color: '#000000' },
  width: 30,
  placement: 'left' as const,
  upVolume: 'rgb(38, 198, 218)',
  downVolume: 'rgb(236, 64, 122)',
  valueAreaUp: 'rgba(38, 198, 218, 0.3)',
  valueAreaDown: 'rgba(236, 64, 122, 0.3)',
  histogramBox: 'rgba(120, 123, 134, 0.1)',
  vah: {
    show: false,
    strokeStyle: '#787B86',
    strokeWidth: 1,
    strokeDasharray: 'Dash' as const,
  },
  val: {
    show: false,
    strokeStyle: '#787B86',
    strokeWidth: 1,
    strokeDasharray: 'Dash' as const,
  },
  poc: {
    show: true,
    strokeStyle: '#b72020',
    strokeWidth: 1,
    strokeDasharray: 'Solid' as const,
  },
  developingPOC: {
    show: false,
    strokeStyle: '#b72020',
    strokeWidth: 1,
    strokeDasharray: 'Dash' as const,
  },
  developingVA: {
    show: false,
    strokeStyle: '#787B86',
    strokeWidth: 1,
    strokeDasharray: 'Dash' as const,
  },
};

// Default values for each shape, using interfaces for type safety
export const defaultShapePropertyValues: {
  [K in keyof ShapePropsByType]?: Partial<ShapePropsByType[K]>;
} = {
  [ShapeType.TREND_LINE]: {
    type: ShapeType.TREND_LINE,
    // coordinates: [{ x: 0, y: 0 }, { x: 0, y: 0 }],
    line: { strokeWidth: 2, strokeDasharray: 'Solid', strokeStyle: '#2962ff' },
    arrow: [VertexType.NORMAL, VertexType.NORMAL],
    extend: [false, false],
    showPriceRange: false,
    showPercentChange: false,
    showBarsRange: false,
    showDateTimeRange: false,
    showAngle: false,
    alwaysShow: false,
  },

  [ShapeType.RAY]: {
    type: ShapeType.RAY,
    // coordinates: [{ x: 0, y: 0 }, { x: 0, y: 0 }],
    line: { strokeWidth: 2, strokeDasharray: 'Solid', strokeStyle: '#2962ff' },
    arrow: [VertexType.NORMAL, VertexType.NORMAL],
    extend: [false, true],
    showPriceRange: false,
    showPercentChange: false,
    showBarsRange: false,
    showDateTimeRange: false,
    showAngle: false,
    alwaysShow: false,
  },

  [ShapeType.INFO_LINE]: {
    type: ShapeType.INFO_LINE,
    // coordinates: [{ x: 0, y: 0 }, { x: 0, y: 0 }],
    line: { strokeWidth: 2, strokeDasharray: 'Solid', strokeStyle: '#2962ff' },
    arrow: [VertexType.NORMAL, VertexType.NORMAL],
    extend: [false, false],
    showPriceRange: true,
    showPercentChange: true,
    showBarsRange: true,
    showDateTimeRange: true,
    showAngle: true,
    alwaysShow: true,
  },

  [ShapeType.HORZ_LINE]: {
    type: ShapeType.HORZ_LINE,
    // y: 0,
    line: { strokeWidth: 2, strokeDasharray: 'Solid', strokeStyle: '#2962ff' },
  },

  [ShapeType.VERT_LINE]: {
    type: ShapeType.VERT_LINE,
    // x: 0,
    line: { strokeWidth: 2, strokeDasharray: 'Solid', strokeStyle: '#2962ff' },
  },

  [ShapeType.FIB_RETRACEMENT]: {
    type: ShapeType.FIB_RETRACEMENT,
    // coordinates: [{ x: 0, y: 0 }, { x: 0, y: 0 }],
    trendLine: { strokeWidth: 1, strokeDasharray: 'Dash', strokeStyle: '#787b86' },
    levelsLine: { strokeWidth: 1, strokeDasharray: 'Solid' },
    extend: [false, false],
    levels: retracementsPropetyWithColor,
    background: { show: true, opacity: 0.2 },
  },

  [ShapeType.FIB_CHANNEL]: {
    type: ShapeType.FIB_CHANNEL,
    // coordinates: [{ x: 0, y: 0 }, { x: 0, y: 0 }],
    levelsLine: { strokeWidth: 1, strokeDasharray: 'Solid' },
    extend: [false, false],
    levels: retracementsPropetyWithColor,
    background: { show: true, opacity: 0.2 },
  },

  [ShapeType.GANN_BOX]: {
    type: ShapeType.GANN_BOX,
    // coordinates: [{ x: 0, y: 0 }, { x: 0, y: 0 }],
    priceLevels: levelsPropetyWithColor,
    priceBackground: { show: true, opacity: 0.2 },
    timeLevels: levelsPropetyWithColor,
    timeBackground: { show: true, opacity: 0.2 },
  },

  [ShapeType.GANN_FAN]: {
    type: ShapeType.GANN_FAN,
    // coordinates: [{ x: 0, y: 0 }, { x: 0, y: 0 }],
    levels: fansWithColor,
    background: { show: true, opacity: 0.2 },
  },

  [ShapeType.ELLIOTT_IMPULSE_12345]: {
    type: ShapeType.ELLIOTT_IMPULSE_12345,
    // coordinates: [{ x: 0, y: 0 }, { x: 0, y: 0 }],
    line: { strokeWidth: 2, strokeDasharray: 'Solid', strokeStyle: '#2962ff' },
  },

  [ShapeType.TEXT]: {
    type: ShapeType.TEXT,
    // coordinate: { x: 0, y: 0 },
    text: '',
    font: {
      fill: 'rgba(29, 85, 215,1)',
      fontFamily: 'Arial',
      fontSize: 12,
      fontWeight: 'normal',
      fontStyle: 'normal',
    },
    background: { show: true, color: 'rgba(120, 123, 134,0.2)' },
  },

  [ShapeType.RECTANGLE]: {
    type: ShapeType.RECTANGLE,
    // coordinates: [{ x: 0, y: 0 }, { x: 0, y: 0 }],
    border: { strokeWidth: 2, strokeDasharray: 'Solid', strokeStyle: '#2962ff' },
    middle: { show: false, strokeWidth: 1, strokeDasharray: 'Dash', strokeStyle: 'rgb(156, 39, 176)' },
    background: { show: true, color: 'rgba(156, 39, 176, 0.2)' },
    extend: [false, false],
  },
  [ShapeType.TRIANGLE]: {
    type: ShapeType.TRIANGLE,
    // coordinates: [{ x: 0, y: 0 }, { x: 0, y: 0 }],
    border: { strokeWidth: 2, strokeDasharray: 'Solid', strokeStyle: '#2962ff' },
    background: { show: true, color: 'rgba(156, 39, 176, 0.2)' },
  },
  [ShapeType.PATH]: {
    type: ShapeType.PATH,
    // coordinates: [{ x: 0, y: 0 }, { x: 0, y: 0 }],
    line: { strokeWidth: 2, strokeDasharray: 'Solid', strokeStyle: '#2962ff' },
    arrow: [VertexType.NORMAL, VertexType.ARROW],
  },
  [ShapeType.ELLIOTT_CORRECTION_ABC]: {
    type: ShapeType.ELLIOTT_CORRECTION_ABC,
    // coordinates: [{ x: 0, y: 0 }, { x: 0, y: 0 }],
    line: { strokeWidth: 2, strokeDasharray: 'Solid', strokeStyle: '#2962ff' },
  },
  [ShapeType.PARALLEL_CHANNEL]: {
    type: ShapeType.PARALLEL_CHANNEL,
    // coordinates: [{ x: 0, y: 0 }, { x: 0, y: 0 }],
    channel: { strokeWidth: 2, strokeDasharray: 'Solid', strokeStyle: '#2962ff' },
    middle: { show: false, strokeWidth: 1, strokeDasharray: 'Dash', strokeStyle: 'rgb(156, 39, 176)' },
    background: { show: true, color: 'rgba(156, 39, 176, 0.2)' },
    extend: [false, false],
  },
  [ShapeType.XABCD_PATTERN]: {
    type: ShapeType.XABCD_PATTERN,
    // coordinates: [{ x: 0, y: 0 }, { x: 0, y: 0 }],
    border: { strokeWidth: 2, strokeDasharray: 'Solid', strokeStyle: '#2962ff' },
    background: { show: true, color: 'rgba(156, 39, 176, 0.05)' },
    label: {
      fill: 'rgba(255, 255, 255, 1)',
      fontFamily: 'Arial',
      fontSize: 12,
      fontWeight: 'normal',
      fontStyle: 'normal',
    },
  },
  [ShapeType.FIXED_RANGE_VOLUME_PROFILE]: {
    type: ShapeType.FIXED_RANGE_VOLUME_PROFILE,
    ...volumeProfileDefaults,
  },
  [ShapeType.ANCHORED_VOLUME_PROFILE]: {
    type: ShapeType.ANCHORED_VOLUME_PROFILE,
    ...volumeProfileDefaults,
  },
};

//fixed properties of extentible infolines
export const fixedAppearance = {
  edgeStroke: '#1e53e6',
  edgeFill: '#FFFFFF',
  r: 5,
  edgeStrokeWidth: 2,
};
